import { LinkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DragDrop } from '@/components/drag-drop';
import { useForm, Controller } from 'react-hook-form';
import { useCreateAgent } from '@/features/agents/hooks/use-create-agent';
import { ObjectParams } from '@/hooks/use-relation-insert';
import { useNavigate } from 'react-router-dom';
import { DefaultValues } from '@/features/agents/components/capabilities-studio-widget';
import { Navigation } from '@/features/agents/components/navigation';
import { MagicButton } from '@/features/agents/components/magic-button';
import { JsonEditor } from '@/components/json-editor';
import { useObjectJson } from '@/hooks/use-object-json';
import { PromptBuilder } from '@/components/prompt-builder';
import { RadioGrid } from '@/components/radio-grid';
import { useEffect, useState } from 'react';
import { useEmitter } from '@/hooks/use-emitter';
import { useQueryClient } from '@tanstack/react-query';
import { UploadAgentForm } from '@/features/agents/components/upload-agent-form';
import { CreateMode, CreateModeToggle } from './create-mode-toggle';

type CreateFormProps = {
  onProceed: (data: DefaultValues) => void;
  steps: { id: string; href: string; name: string; status: string }[];
  id: string;
};

interface RelationField {
  uuid: string;
  container: string;
}

type CreateFormInputs = {
  title: string;
  type?: string;
  need?: string;
  testInput?: RelationField;
  inputObject?: string;
  expectedTestOutput?: RelationField;
  result?: string;
};

export function CreateForm({ id, onProceed, steps }: CreateFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateFormInputs>();
  const queryClient = useQueryClient();
  const createAgentMutation = useCreateAgent();
  const navigate = useNavigate();
  const idPrefix = id.split('/').slice(0, -1).join('/');
  const emit = useEmitter({
    id,
    listenersPrefix: idPrefix,
  });
  const [mode, setMode] = useState<CreateMode>('generate');

  useEffect(() => {
    emit({
      type: 'preview/html',
      data: {
        html: '',
      },
    });
  }, []);

  function onSubmit(data: CreateFormInputs): void {
    const createData = {
      sourceExample: data.testInput,
      inputObject: data.inputObject,
      targetExample: data.expectedTestOutput,
      need: data.need,
      result: data.result,
      type: data.type,
      title: data.title,
    };

    createAgentMutation.mutate(createData, {
      onSuccess: (data) => {
        onProceed({
          sourceExample: createData.sourceExample,
          inputObject: createData.inputObject,
        });

        queryClient.invalidateQueries({ queryKey: ['userAgents'] });

        navigate(`/agents/${data.uuid}/test`);
      },
    });
  }

  function handleDrop(
    { value }: { value: ObjectParams[] },
    field: keyof CreateFormInputs
  ) {
    setValue(field, { uuid: value[0].uuid, container: value[0].container });
  }

  const testInput = watch('testInput');
  const expectedTestOutput = watch('expectedTestOutput');

  const { data: testInputData } = useObjectJson(
    testInput?.uuid || '',
    testInput?.container || '',
    {
      refetchInterval: 0,
    }
  );

  if (mode === 'upload') {
    return (
      <>
        <Navigation steps={steps} />

        <div className="p-7">
          <CreateModeToggle mode={mode} onChange={setMode} />
          <UploadAgentForm />
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation
        loadingStep={createAgentMutation.isLoading ? 'create' : undefined}
        steps={steps}
      />

      <div className="p-7">
        <CreateModeToggle mode={mode} onChange={setMode} />
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid grid-cols-2 gap-y-4 gap-x-7"
        >
          <div className="flex flex-col gap-y-4">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Title
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="title"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
                  {...register('title', { required: true })}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">
                    Title is required.
                  </p>
                )}
              </div>
            </div>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <RadioGrid
                  maxHeight={714}
                  label="Type"
                  options={[
                    {
                      value: '1',
                      label: 'Transformer',
                      thumbnail:
                        'https://img.freepik.com/free-vector/gradient-api-illustration_23-2149379182.jpg?semt=ais_hybrid&w=740',
                    },
                    {
                      value: '2',
                      label: 'Data fetcher',
                      thumbnail:
                        'https://img.freepik.com/free-vector/gradient-api-illustration_23-2149379182.jpg?semt=ais_hybrid&w=740',
                    },
                    {
                      value: '3',
                      label: 'Anything',
                      thumbnail:
                        'https://img.freepik.com/free-vector/gradient-api-illustration_23-2149379182.jpg?semt=ais_hybrid&w=740',
                    },
                    {
                      value: '5',
                      label: 'Use PIM data',
                      thumbnail:
                        'https://components.dyta.app/pim_valantic_viamedici.jpg',
                    },
                  ]}
                  {...field}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-y-4">
            <div>
              <label
                htmlFor="need"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Need
              </label>
              <PromptBuilder
                className="mt-1"
                onChange={(value) => setValue('need', value)}
              />
              <input id="need" type="hidden" {...register('need')} />
              {errors.need && (
                <p className="text-red-500 text-sm mt-1">Need is required.</p>
              )}
            </div>

            <div>
              <label
                htmlFor="result"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Result
              </label>
              <PromptBuilder
                className="mt-1"
                onChange={(value) => setValue('result', value)}
              />
              <input id="result" type="hidden" {...register('result')} />
              {errors.result && (
                <p className="text-red-500 text-sm mt-1">Result is required.</p>
              )}
            </div>
            <div>
              <label
                htmlFor="testInput"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Test Input
              </label>
              <div className="space-y-1 mt-2">
                <DragDrop
                  allowedTypes={['object/insert']}
                  onDrop={(data) => handleDrop(data, 'testInput')}
                  className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-600 border-dashed justify-center text-center h-5"
                  droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-700 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-600 border-dashed bg-purple-50-900/50 flex justify-center items-center h-5"
                >
                  {({ isDropping }) =>
                    isDropping ? (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    )
                  }
                </DragDrop>
                {testInput?.uuid && testInput?.container ? (
                  <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 py-2 pl-2 pr-3 shadow-sm text-xs">
                    <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/50 rounded-full flex justify-center items-center text-center">
                      <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {testInput.container}
                      </p>
                      <p className="truncate text-gray-500 dark:text-gray-400">
                        {testInput.uuid}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setValue('testInput', { uuid: '', container: '' })
                      }
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}

                <JsonEditor data={JSON.parse(testInputData?.data || '{}')} />

                <input type="hidden" {...register('testInput')} />
                {errors.testInput && (
                  <p className="text-red-500 text-sm mt-1">
                    Test Input is required.
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="inputObject"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Test Input Free Text
              </label>
              <div className="mt-1">
                <textarea
                  id="inputObject"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
                  {...register('inputObject')}
                ></textarea>
                {errors.inputObject && (
                  <p className="text-red-500 text-sm mt-1">
                    Input Object Free Text is required.
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="expectedTestOutput"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Expected Test Output
              </label>
              <div className="space-y-1 mt-2">
                <DragDrop
                  allowedTypes={['object/insert']}
                  onDrop={(data) => handleDrop(data, 'expectedTestOutput')}
                  className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-700  px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-600 border-dashed justify-center text-center h-5"
                  droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-700 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-600 border-dashed bg-purple-50  flex justify-center items-center h-5"
                >
                  {({ isDropping }) =>
                    isDropping ? (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    ) : (
                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                    )
                  }
                </DragDrop>
                {expectedTestOutput?.uuid && expectedTestOutput?.container ? (
                  <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-700 py-2 pl-2 pr-3 shadow-sm text-xs">
                    <div className="relative w-10 h-10 bg-purple-50  rounded-full flex justify-center items-center text-center">
                      <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {expectedTestOutput.container}
                      </p>
                      <p className="truncate text-gray-500 dark:text-gray-400">
                        {expectedTestOutput.uuid}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setValue('expectedTestOutput', {
                          uuid: '',
                          container: '',
                        })
                      }
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
                <input type="hidden" {...register('expectedTestOutput')} />
                {errors.expectedTestOutput && (
                  <p className="text-red-500 text-sm mt-1">
                    Expected Test Output is required.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <MagicButton label="Create" type="submit" />
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
